import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHistory, FaPlus, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { fetchRounds, fetchScores } from '../utils/api';
import './Rounds.css';

const Rounds = () => {
  const [rounds, setRounds] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [roundsData, scoresData] = await Promise.all([
          fetchRounds(),
          fetchScores()
        ]);
        
        if (roundsData.error) {
          setError(roundsData.message);
          return;
        }
        
        if (scoresData.error) {
          setError(scoresData.message);
          return;
        }
        
        setRounds(roundsData);
        setScores(scoresData);
        
        // Extract unique courses
        const uniqueCourses = [...new Set(roundsData.map(round => round.course_name))];
        setCourses(uniqueCourses);
      } catch (err) {
        setError('Failed to load rounds. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group scores by round
  const scoresByRound = {};
  scores.forEach(score => {
    if (!scoresByRound[score.round_id]) {
      scoresByRound[score.round_id] = [];
    }
    scoresByRound[score.round_id].push(score);
  });

  // Filter rounds
  const filteredRounds = rounds.filter(round => {
    if (courseFilter !== 'all' && round.course_name !== courseFilter) {
      return false;
    }
    
    // No additional filtering for "all"
    if (filter === 'all') {
      return true;
    }
    
    // Convert to date and filter by month/year
    const roundDate = new Date(round.date);
    const currentDate = new Date();
    
    if (filter === 'thisMonth') {
      return roundDate.getMonth() === currentDate.getMonth() && 
             roundDate.getFullYear() === currentDate.getFullYear();
    }
    
    if (filter === 'thisYear') {
      return roundDate.getFullYear() === currentDate.getFullYear();
    }
    
    return true;
  });

  // Sort rounds by date (newest first)
  filteredRounds.sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) {
    return (
      <div className="container">
        <div className="page-title">
          <FaHistory />
          <h1>Rundor</h1>
        </div>
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="page-title">
          <FaHistory />
          <h1>Rundor</h1>
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  // Format date 
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sv-SE', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="container">
      <div className="page-header">
        <div className="page-title">
          <FaHistory />
          <h1>Rundor</h1>
        </div>
        <Link to="/add-round" className="btn">
          <FaPlus /> Lägg till runda
        </Link>
      </div>

      <div className="filters-card card">
        <div className="filters">
          <div className="filter-group">
            <label><FaCalendarAlt /> Tidsperiod:</label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Alla</option>
              <option value="thisMonth">Denna månad</option>
              <option value="thisYear">Detta år</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label><FaFilter /> Bana:</label>
            <select 
              value={courseFilter} 
              onChange={(e) => setCourseFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Alla banor</option>
              {courses.map(course => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {filteredRounds.length === 0 ? (
        <div className="card empty-state">
          <p>Inga rundor hittades med valda filter.</p>
        </div>
      ) : (
        filteredRounds.map(round => (
          <div key={round.id} className="round-card card">
            <div className="round-header">
              <h2>{round.course_name}</h2>
              <div className="round-date">
                <FaCalendarAlt />
                <span>{formatDate(round.date)}</span>
              </div>
            </div>
            
            <div className="table-responsive">
              <table className="scores-table">
                <thead>
                  <tr>
                    <th>Spelare</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {scoresByRound[round.id]?.sort((a, b) => a.score - b.score).map(score => (
                    <tr key={score.id}>
                      <td>{score.player_name}</td>
                      <td>{score.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default Rounds; 